'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { CONSENT_BANNER_ELEMENT_ID } from '@/constants/cookie-consent';

/**
 * Height of the visible CookiePro consent banner, or `0` when it is absent or dismissed.
 *
 * Two behaviours of the OneTrust SDK shape this:
 * - The banner is `position: fixed`, so `offsetParent` is `null` even while it is on screen.
 *   Visibility is therefore read from the box height, never from `offsetParent`.
 * - Dismissing it leaves the element in the DOM with `display: none`, which a `ResizeObserver`
 *   reports as a zero-height box.
 *
 * `enabled` keeps the observers off for visitors who cannot use the measurement.
 */
export function useConsentBannerHeight(enabled) {
  const $ = _c(3);
  const [height, setHeight] = useState(0);
  let t0;
  let t1;
  if ($[0] !== enabled) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      let tracked = null;
      let hasAppeared = false;
      const resizeObserver = new ResizeObserver(() => measure());
      const mutationObserver = new MutationObserver(() => trackBanner());
      const stopObserving = () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
      };
      function measure() {
        const next = tracked ? Math.round(tracked.getBoundingClientRect().height) : 0;
        setHeight(next);
        if (hasAppeared && next === 0) {
          stopObserving();
        }
        if (next > 0) {
          hasAppeared = true;
        }
      }
      function trackBanner() {
        const banner = document.getElementById(CONSENT_BANNER_ELEMENT_ID);
        if (banner === tracked) {
          return;
        }
        if (tracked) {
          resizeObserver.unobserve(tracked);
        }
        if (banner) {
          resizeObserver.observe(banner);
        }
        tracked = banner;
        measure();
      }
      trackBanner();
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      return stopObserving;
    };
    t1 = [enabled];
    $[0] = enabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return enabled ? height : 0;
}